import { useEffect, useState } from 'react'
import { searchActors, getActorMovies, getMovieCast, profileUrl, posterUrl } from '../../api/tmdb'
import { MODE_NAMES, MODE_ICONS } from './modeMeta'
import {
  IconEye, IconPlay, IconArrowRight,
} from '../icons'

const MODE_RULES = {
  'battle-royale': [
    'Every player races simultaneously on their own device',
    'Connect the start actor to the goal actor',
    'Fewest hops wins — time breaks ties',
    'First one done takes the crown',
  ],
  'speed-run': [
    'Every player races simultaneously',
    'Connect the start actor to the goal actor',
    "Fastest player wins — hops don't matter",
    'Pure speed — no second chances',
  ],
  'co-op': [
    "You're all on the same team",
    'Take turns — pick a movie then an actor, then pass',
    'Work together to reach the goal',
    'One chain, one score, one team',
  ],
  'h2h': [
    'Teams race simultaneously',
    'Take turns picking for your team',
    'First team to reach the goal wins',
    'Fewest hops takes it for your team',
  ],
  'directors-cut': [
    'You are the Agents — pitch your best picks!',
    'Nominate movies and actors each round',
    'The Director decides what makes the cut',
    'Score points when your nominations are chosen',
    'Most points = Agent of the Year',
  ],
  'trilogy': [
    'Three rounds, three puzzles — one champion',
    'Each round: two random players secretly pick the actors — a coin flip decides start vs. goal',
    'Everyone races simultaneously to connect them in as few hops as possible',
    'Scores carry over — fewest total hops across all 3 rounds wins',
    'Equal hops? Lowest total time takes the crown',
    'Picker tip: pick a well-connected actor to help — or an obscure one to trip everyone up',
  ],
}

const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3C/svg%3E"
const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='342' height='513' viewBox='0 0 342 513'%3E%3Crect fill='%23333' width='342' height='513'/%3E%3C/svg%3E"

/**
 * Party Mode instructions screen — fires between cinematic reveal and LCA.
 *
 * role: 'host' | 'player'
 * onFireLCA: host-only callback that writes status: 'countdown' and routes
 *            the host gameState back to 'party-actor-pick' for LCA.
 *
 * Multi-screen flow:
 *   1. Attention grabber (3-second auto-advance)
 *   2. Live demo (host: ~17s auto-playing chain build; players: "watch the host")
 *   3. Mode-specific rules
 *   4. Ready screen — host taps "Action!" or "Skip" to fire LCA
 *
 * Skip is visible from the first second on every host screen.
 */
export default function PartyInstructions({ party, role, onFireLCA }) {
  const isHost = role === 'host'
  const mode = party?.mode
  const ModeIcon = MODE_ICONS[mode]

  // Multi-screen state machine
  const [screen, setScreen] = useState('attention') // 'attention' | 'demo' | 'rules' | 'ready'
  const [demoStep, setDemoStep] = useState(0)
  const [demoData, setDemoData] = useState({ start: null, m1: null, mid: null, m2: null, goal: null })

  // ── Demo TMDB fetch — best effort, fail open ──────────────────────────────
  useEffect(() => {
    if (!isHost) return
    let cancelled = false
    ;(async () => {
      try {
        const carellHits = await searchActors('Steve Carell')
        const carell = carellHits?.[0]
        if (!carell || cancelled) return
        const carellMovies = await getActorMovies(carell.id)
        const csl = carellMovies.find(m => /crazy.{0,3}stupid.{0,3}love/i.test(m.title))
        if (!csl || cancelled) return setDemoData((d) => ({ ...d, start: carell, m1: csl }))
        const cslCast = await getMovieCast(csl.id)
        const gosling = cslCast.find(a => /ryan\s+gosling/i.test(a.name))
        if (!gosling || cancelled) return setDemoData((d) => ({ ...d, start: carell, m1: csl }))
        const goslingMovies = await getActorMovies(gosling.id)
        const nb = goslingMovies.find(m => /^the\s+notebook$/i.test(m.title))
        if (!nb || cancelled) return setDemoData((d) => ({ ...d, start: carell, m1: csl, mid: gosling }))
        const nbCast = await getMovieCast(nb.id)
        const mcadams = nbCast.find(a => /rachel\s+mcadams/i.test(a.name))
        if (cancelled) return
        setDemoData({ start: carell, m1: csl, mid: gosling, m2: nb, goal: mcadams || null })
      } catch {
        // Fail silently — demo will fall back to placeholder text
      }
    })()
    return () => { cancelled = true }
  }, [isHost])

  // ── Screen 1: attention — auto-advance after 3s ──────────────────────────
  useEffect(() => {
    if (screen !== 'attention') return
    const id = setTimeout(() => setScreen('demo'), 3000)
    return () => clearTimeout(id)
  }, [screen])

  // ── Screen 2: demo step auto-advance (host only) ─────────────────────────
  useEffect(() => {
    if (screen !== 'demo' || !isHost) return
    if (demoStep >= 6) {
      // Final step — advance to rules after a beat
      const id = setTimeout(() => setScreen('rules'), 3000)
      return () => clearTimeout(id)
    }
    const id = setTimeout(() => setDemoStep((s) => s + 1), 2500)
    return () => clearTimeout(id)
  }, [screen, demoStep, isHost])

  // ── Screen 3: rules auto-advance to ready after 6s ───────────────────────
  useEffect(() => {
    if (screen !== 'rules') return
    const id = setTimeout(() => setScreen('ready'), 6000)
    return () => clearTimeout(id)
  }, [screen])

  // ── Host actions ─────────────────────────────────────────────────────────
  const fireLCA = () => {
    if (!isHost) return
    onFireLCA?.()
  }

  // ── Render: PLAYER view (always the same) ─────────────────────────────────
  if (!isHost) {
    if (screen === 'ready' || screen === 'rules' || demoStep >= 6) {
      return (
        <div className="party-instructions party-instructions--player">
          <h2 className="party-instructions__player-headline">Get ready...</h2>
          {party?.startActor && party?.goalActor && (
            <p className="party-instructions__player-matchup">
              <strong>{party.startActor.name}</strong> → <strong>{party.goalActor.name}</strong>
            </p>
          )}
          <p className="party-instructions__pulse">Action coming up...</p>
        </div>
      )
    }
    if (screen === 'attention') {
      return (
        <div className="party-instructions party-instructions--player">
          <h2 className="party-instructions__player-headline">
            <IconEye size={22} aria-hidden="true" />
            <span>Watch the host screen!</span>
          </h2>
          <p className="party-instructions__player-mode">
            {ModeIcon && <ModeIcon size={15} aria-hidden="true" />}
            <span>{MODE_NAMES[mode] || mode}</span>
          </p>
        </div>
      )
    }
    return (
      <div className="party-instructions party-instructions--player">
        <h2 className="party-instructions__player-headline">
          <IconEye size={22} aria-hidden="true" />
          <span>Watch the host screen...</span>
        </h2>
      </div>
    )
  }

  // ── Render: HOST view (multi-screen) ──────────────────────────────────────
  // Skip button — always visible from the very first frame
  const SkipBar = (
    <div className="party-instructions__skip-bar">
      <button type="button" className="btn-ghost party-instructions__skip-btn" onClick={fireLCA}>
        <span>Skip</span>
        <IconArrowRight size={14} aria-hidden="true" />
      </button>
    </div>
  )

  if (screen === 'attention') {
    return (
      <div className="party-instructions party-instructions--host">
        {SkipBar}
        <h1 className="party-instructions__big-title">Welcome to Action!</h1>
        <p className="party-instructions__big-sub">The game where you Connect the Stars!</p>
        <p className="party-instructions__big-tag">Let's learn how to play...</p>
      </div>
    )
  }

  if (screen === 'demo') {
    return (
      <div className="party-instructions party-instructions--host party-instructions--demo">
        {SkipBar}
        <DemoStage step={demoStep} data={demoData} />
      </div>
    )
  }

  if (screen === 'rules') {
    const rules = MODE_RULES[mode] || []
    return (
      <div className="party-instructions party-instructions--host">
        {SkipBar}
        <h2 className="party-instructions__rules-title">
          {ModeIcon && <ModeIcon size={28} aria-hidden="true" />}
          <span>{MODE_NAMES[mode] || mode}</span>
        </h2>
        <ol className="party-instructions__rules-list">
          {rules.map((r, i) => (
            <li key={i}>
              <span className="party-instructions__rules-num">{i + 1}</span>
              <span className="party-instructions__rules-text">{r}</span>
            </li>
          ))}
        </ol>
      </div>
    )
  }

  // 'ready'
  return (
    <div className="party-instructions party-instructions--host">
      {SkipBar}
      <h2 className="party-instructions__ready-title">Ready to play?</h2>
      {party?.startActor && party?.goalActor && (
        <div className="party-instructions__matchup">
          <div className="party-instructions__matchup-actor">
            <img
              src={party.startActor.profile_path ? profileUrl(party.startActor.profile_path) : ACTOR_PH}
              alt={party.startActor.name}
            />
            <p className="party-instructions__matchup-label">START</p>
            <p className="party-instructions__matchup-name">{party.startActor.name}</p>
          </div>
          <span className="party-instructions__matchup-arrow" aria-hidden="true">
            <IconArrowRight size={28} />
          </span>
          <div className="party-instructions__matchup-actor">
            <img
              src={party.goalActor.profile_path ? profileUrl(party.goalActor.profile_path) : ACTOR_PH}
              alt={party.goalActor.name}
            />
            <p className="party-instructions__matchup-label">GOAL</p>
            <p className="party-instructions__matchup-name">{party.goalActor.name}</p>
          </div>
        </div>
      )}
      <button type="button" className="btn-primary party-instructions__action-btn" onClick={fireLCA}>
        <IconPlay size={18} aria-hidden="true" />
        <span>Everyone's Ready — Action!</span>
      </button>
    </div>
  )
}

// ── DemoStage — auto-plays 7 steps through the canonical chain ─────────────
function DemoStage({ step, data }) {
  const { start, m1, mid, m2, goal } = data
  const captions = [
    'You start with this actor...',
    'Browse their movies and pick one...',
    'Pick your next actor from the cast...',
    'Your chain grows!',
    'Keep going...',
    'Find the goal actor to win!',
    "That's how you Connect the Stars!",
  ]
  const caption = captions[Math.max(0, Math.min(step, captions.length - 1))]

  const Arrow = () => (
    <span className="party-instructions__demo-arrow" aria-hidden="true">
      <IconArrowRight size={22} />
    </span>
  )

  return (
    <div className="party-instructions__demo">
      <div className="party-instructions__demo-stage">
        <ChainSlot kind="actor" data={start} highlight={step === 0} />
        {step >= 1 && (
          <>
            <Arrow />
            <ChainSlot kind="movie" data={m1} highlight={step === 1} />
          </>
        )}
        {step >= 2 && (
          <>
            <Arrow />
            <ChainSlot kind="actor" data={mid} highlight={step === 2} />
          </>
        )}
        {step >= 4 && (
          <>
            <Arrow />
            <ChainSlot kind="movie" data={m2} highlight={step === 4} />
          </>
        )}
        {step >= 5 && (
          <>
            <Arrow />
            <ChainSlot kind="actor" data={goal} highlight={step >= 5} goal />
          </>
        )}
      </div>
      <p className="party-instructions__demo-caption">{caption}</p>
    </div>
  )
}

function ChainSlot({ kind, data, highlight, goal }) {
  if (!data) {
    return (
      <div className={`party-instructions__demo-slot${highlight ? ' party-instructions__demo-slot--highlight' : ''}`}>
        <div className="party-instructions__demo-img party-instructions__demo-img--placeholder">…</div>
      </div>
    )
  }
  const src = kind === 'actor'
    ? (data.profile_path ? profileUrl(data.profile_path) : ACTOR_PH)
    : (data.poster_path  ? posterUrl(data.poster_path)   : MOVIE_PH)
  const label = kind === 'actor' ? data.name : data.title
  return (
    <div className={[
      'party-instructions__demo-slot',
      highlight ? 'party-instructions__demo-slot--highlight' : '',
      goal ? 'party-instructions__demo-slot--goal' : '',
    ].join(' ').trim()}>
      <img className="party-instructions__demo-img" src={src} alt={label} />
      <p className="party-instructions__demo-label">{label}</p>
    </div>
  )
}
